// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import ShieldIcon from '@mui/icons-material/Shield'
import HubIcon from '@mui/icons-material/Hub'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function FocusSection() {
  const { t } = useTranslation('technology')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const iconSx = { fontSize: '1.9rem', color: themeColors.bg.start }

  const areas: { icon: ReactElement; title: string; description: string }[] = [
    {
      icon: <ShieldIcon sx={iconSx} />,
      title: t('focus.items.01.title'),
      description: t('focus.items.01.description')
    },
    {
      icon: <HubIcon sx={iconSx} />,
      title: t('focus.items.02.title'),
      description: t('focus.items.02.description')
    },
    {
      icon: <RestartAltIcon sx={iconSx} />,
      title: t('focus.items.03.title'),
      description: t('focus.items.03.description')
    },
    {
      icon: <BuildCircleIcon sx={iconSx} />,
      title: t('focus.items.04.title'),
      description: t('focus.items.04.description')
    }
  ]

  const getGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('focus.eyebrow')}
        title={t('focus.title')}
        intro={[t('focus.intro')]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {areas.map(area => (
          <Box
            key={area.title}
            sx={{
              height: '100%',
              background: themeColors.section.light,
              border: `1px solid ${themeColors.gray.lighter}`,
              borderRadius: '10px',
              padding: '1.75rem',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ lineHeight: 0, marginBottom: '1.1rem' }}>
              {area.icon}
            </Box>

            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: '1.05rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: themeColors.gray.dark,
                marginBottom: '0.7rem'
              }}
            >
              {area.title}
            </Typography>

            <Typography
              component="p"
              sx={{
                fontSize: getFontBodySmall(),
                lineHeight: 1.65,
                color: themeColors.gray.defaultText
              }}
            >
              {area.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
